import type { AuthorityRole } from '@/models/authorityPosition';
import type { BankAccount } from '@/features/settings/types';

export type TitheSubjectType = 'position' | 'district' | 'region' | 'institution';
export type TithePaymentMethod = 'cash' | 'check' | 'bank_transfer';

/** One payment line on a tithe (split payment support — e.g. part cash, part QR). */
export interface TithePayment {
  id: string;
  tithe_id: string;
  method: TithePaymentMethod;
  amount: number;
  bank_account_id: string | null;
  check_number: string | null;
  bank_account?: BankAccount | null;
}

/** One payment line as entered in the form, before numeric parsing. */
export interface TithePaymentInput {
  method: TithePaymentMethod | '';
  amount: string;
  bank_account_id: string;
  check_number: string;
}

export interface Tithe {
  id: string;
  authority_position_id: string | null;
  district_id: string | null;
  region_id: string | null;
  institution_slug: string | null;
  year: number;
  month: number;        // 1–12
  amount: number;
  paid_at: string;      // ISO date
  received_by: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  // Payer
  payer_name: string | null;
  // USD
  amount_usd: number | null;
  exchange_rate: number | null;
  // Voucher
  voucher_photo_url: string | null;
  voucher_transaction_ref: string | null;
  voucher_depositor_name: string | null;
  voucher_dest_account: string | null;
  // Joined — one or more payment lines (split payment support)
  payments?: TithePayment[];
  // Joined (for position-based tithes)
  authority_position?: {
    id: string;
    org_type: 'DISTRICT' | 'REGION' | 'NATIONAL';
    district_id: string | null;
    region_id: string | null;
    role: AuthorityRole;
    started_at: string;
    ended_at: string | null;
    minister: {
      id: string;
      adb_code: string;
      full_name: string | null;
    };
    district?: { id: string; name: string | null; code: string | null };
    region?: { id: string; name: string | null; code: string | null; district_id: string };
  };
  // Joined (for district-level tithes via tithes.district_id)
  tithe_district?: { id: string; name: string | null; code: string | null } | null;
  // Joined (for region-level tithes via tithes.region_id)
  tithe_region?: {
    id: string;
    name: string | null;
    code: string | null;
    district_id: string;
  } | null;
}

export interface TitheFormData {
  subjectType: TitheSubjectType;
  subjectId: string;
  year: number;
  month: number;
  amount: string;
  paid_at: string;
  notes: string;
  payer_name: string;
  amount_usd: string;
  exchange_rate: string;
  voucher_photo_url: string;
  voucher_transaction_ref: string;
  voucher_depositor_name: string;
  voucher_dest_account: string;
}

export type TitheScope = 'NATIONAL' | 'DISTRICT' | 'REGION' | 'INSTITUTION';

export interface TitheFilters {
  year: number;
  scope: TitheScope;
  districtId: string | null;
  regionId: string | null;
}

export const MONTH_NAMES = [
  'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
  'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC',
];

export const MONTH_NAMES_FULL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/** Returns the last day of the given month (1-indexed) for a given year */
export function lastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 0); // day=0 of next month = last day of this month
}

/** Last day of the month as 'YYYY-MM-DD', comparable with a date column without timezone parsing */
export function lastDayOfMonthISO(year: number, month: number): string {
  const d = lastDayOfMonth(year, month);
  return `${year}-${String(month).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** True when paid_at is after the last day of that tithe's month */
export function isLate(tithe: Tithe): boolean {
  // String compare: new Date('YYYY-MM-DD') parses as UTC and misflags the last day in Bolivia (UTC-4).
  return tithe.paid_at.slice(0, 10) > lastDayOfMonthISO(tithe.year, tithe.month);
}
